<template>
  <div class="control-list-page">
    <h1>Control Room</h1>
    <div v-if="loading">Loading previous links...</div>
    <div v-else>
      <section class="public-section" v-if="publicSessions.length">
        <h2>Public Countdowns</h2>
        <p class="subtitle">Anyone can join and control</p>
        <div class="public-grid">
          <div v-for="publicSession in publicSessions" :key="publicSession.id" class="public-card">
            <mini-user
              :online="undefined"
              :fullname="publicSession.owner?.name"
              :photo_url="publicSession.owner?.photo_url"
              :username="publicSession.owner?.username"
              :user_id="publicSession.owner?.id"
            />
            <div class="info">
              <div class="status">Status: {{ publicSession.status }}</div>
              <button class="link-btn" @click="accessPublic(publicSession)">Control</button>
            </div>
          </div>
        </div>
      </section>

      <section class="previous-links-view">
        <h2>Your Toys</h2>
        <p class="subtitle">So submissive!</p>
        <div class="link-grid" v-if="previousLinks.length">
          <ControlLinkDetails
            v-for="link in previousLinks"
            :key="link.control_link_uuid"
            :link="link"
            @access="accessPreviousLink"
          />
        </div>
        <div v-else class="no-session">
          <div class="naughty-message">
            <h2>🔥 Looking for someone to stalk? 😈</h2>
            <p>No previous control links found. Time to find someone new to torment! 😉</p>
            <p class="hint">
              Share your control link with others, or get someone else's link to start the fun!
            </p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { apiFetch } from '@/api'
import { useAuthStore } from '@/stores/auth'
import { useToastStore } from '@/stores/toast'
import ControlLinkDetails, {
  type PreviousControlLinkOut,
} from '@/components/ControlLinkDetails.vue'
import MiniUser from '@/components/MiniUser.vue'
import type { SessionID, SessionUUID, SessionStatus } from '@/stores/ws.ts'
import type { ControllerOut } from '@/schemas/schemas.ts'

interface PublicSessionOut {
  id: SessionID
  uuid: SessionUUID
  countdown_seconds: number
  owner: ControllerOut
  status: SessionStatus
}

const router = useRouter()
const auth = useAuthStore()
const toasts = useToastStore()

const loading = ref(true)
const previousLinks = ref<PreviousControlLinkOut[]>([])
const publicSessions = ref<PublicSessionOut[]>([])

async function loadPreviousLinks() {
  try {
    const resp = await apiFetch('/api/me/previous-control-links')
    if (resp.ok) {
      previousLinks.value = (await resp.json()) as PreviousControlLinkOut[]
    }
  } catch (err) {
    console.error('Failed to load previous links:', err)
  }
}

async function loadPublicSessions() {
  try {
    const resp = await apiFetch('/api/public/active-sessions')
    if (resp.ok) {
      publicSessions.value = (await resp.json()) as PublicSessionOut[]
    }
  } catch (err) {
    console.error('Failed to load public sessions:', err)
  }
}

function accessPreviousLink(linkUuid: string) {
  router.push({ path: '/control', query: { link: linkUuid } })
}

function accessPublic(publicSession: PublicSessionOut) {
  const uuid = publicSession.uuid // TODO: is this the correct uuid to use?
  if (!uuid) return
  router.push({ path: '/control', query: { link: uuid } })
}

onMounted(async () => {
  // Ensure user is authenticated first
  if (!auth.user && auth.token) await auth.loadMe()

  if (!auth.user) {
    toasts.add('Please log in to access the control room', 'info')
    router.push('/login')
    return
  }

  await Promise.all([loadPreviousLinks(), loadPublicSessions()])
  loading.value = false
})
</script>

<style scoped lang="scss">
.control-list-page {
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
  background: var(--color-background);
  color: var(--color-text);
  border-radius: 1rem;
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  .public-section {
    margin-bottom: 2rem;
    .subtitle {
      margin-bottom: 0.75rem;
      color: var(--color-text-muted);
    }
    .public-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
      gap: 1rem;
    }
    .public-card {
      display: flex;
      gap: 0.75rem;
      align-items: center;
      padding: 0.75rem;
      border: 1px solid var(--color-border);
      border-radius: 0.5rem;
      background: var(--color-background-soft);
      .info {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }
      .status {
        font-size: 0.85rem;
        opacity: 0.8;
      }
      .link-btn {
        align-self: start;
        margin-top: 0.25rem;
      }
    }
  }

  .no-session {
    text-align: center;
    margin-top: 2rem;
    .naughty-message {
      h2 {
        font-size: 1.5rem;
        margin-bottom: 0.5rem;
      }
      p {
        margin: 0.25rem 0;
      }
      .hint {
        font-style: italic;
        color: var(--color-text-muted);
      }
    }
  }

  .previous-links-view {
    margin-top: 2rem;
    .subtitle {
      margin-bottom: 1.5rem;
      font-style: italic;
      color: var(--color-text-muted);
    }
    .link-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
      gap: 1rem;
    }
  }
}
</style>
